import { useState, type ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Banner, ConfirmDialog, Field, Input } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useSiteOperation } from '@/hooks/useSites';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { OPERATION_LABELS } from '@/utils/labels';
import type { SiteDetail } from '@/types/api';

/** Removing a site from its server with its data, confirmed by typing its name. */
export function DestroySiteDialog({
  site,
  open,
  onClose,
}: {
  readonly site: SiteDetail;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const [typed, setTyped] = useState('');

  const destroy = useSiteOperation(site.id, {
    onSuccess: () => {
      setTyped('');
      toast.success('Операция поставлена в очередь', OPERATION_LABELS['destroy']);
      onClose();
    },
  });

  return (
    <ConfirmDialog
      open={open}
      title="Удалить сайт?"
      description="Сайт и его данные будут удалены с сервера. Действие необратимо."
      confirmLabel="Удалить сайт"
      destructive
      busy={destroy.isPending}
      error={destroy.isError ? errorText(destroy.error, 'Не удалось удалить сайт') : undefined}
      onConfirm={() =>
        destroy.mutate({
          command: { operation: 'destroy', confirmation: typed.trim(), revision: site.revision },
          key: idempotencyKey('site.destroy'),
        })
      }
      onClose={() => {
        setTyped('');
        onClose();
      }}
    >
      <div className="stack">
        {!site.hasVerifiedBackup && site.status !== 'failed' ? (
          <Banner tone="danger" icon={ShieldAlert}>
            Нет проверенной резервной копии: удаление будет отклонено.
          </Banner>
        ) : null}

        <Field label="Для подтверждения введите идентификатор сайта" htmlFor="destroy-confirm" hint={site.slug}>
          <Input
            id="destroy-confirm"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder={site.slug}
            autoComplete="off"
          />
        </Field>
      </div>
    </ConfirmDialog>
  );
}
